"use client";
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastItem, ToastType, ToastContextValue } from '../utils/types/toast.types';
import ToastContainer from '../components/Toast/ToastContainer';

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
    children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const showToast = useCallback((
        type: ToastType,
        title: string,
        message: string,
        duration: number = 5000
    ): string => {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);

        const newToast: ToastItem = {
            id,
            type,
            title,
            message,
            duration,
        };

        setToasts(prev => [...prev, newToast]);
        return id;
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    // Convenience methods
    const showSuccessToast = useCallback((title: string, message: string, duration?: number) =>
        showToast('success', title, message, duration), [showToast]);

    const showErrorToast = useCallback((title: string, message: string, duration?: number) =>
        showToast('error', title, message, duration), [showToast]);

    const showInfoToast = useCallback((title: string, message: string, duration?: number) =>
        showToast('info', title, message, duration), [showToast]);

    const showWarningToast = useCallback((title: string, message: string, duration?: number) =>
        showToast('warning', title, message, duration), [showToast]);

    const value: ToastContextValue = {
        showToast,
        removeToast,
        showSuccessToast,
        showErrorToast,
        showInfoToast,
        showWarningToast,
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}